import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ccremoteIcon from '../assets/ccremote-icon.png';
import { trackPageView } from '../utils/analytics';

const CCRemoteGuide = () => {
  useEffect(() => {
    trackPageView('CC Remote Guide');
  }, []);

  return (
    <div className="privacy-container guide-container">
      <header className="privacy-header">
        <div className="app-info">
          <div className="app-icon">
            <img src={ccremoteIcon} alt="CC Remote" />
          </div>
          <div className="app-details">
            <h1>CC Remote</h1>
            <p>Remote Control for Claude Code</p>
          </div>
        </div>
        <div className="header-actions">
          <Link to="/ccremote/cn" className="lang-switch">中文</Link>
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </header>

      <main className="privacy-content">
        <div className="privacy-section">
          <h2>What is CC Remote?</h2>
          <p>CC Remote lets you monitor and control Claude Code sessions running on your computer from your iPhone or iPad. It connects over your local network — no cloud services needed.</p>
          <ul className="guide-list">
            <li>Real-time session monitoring dashboard</li>
            <li>Remote terminal with full input support</li>
            <li>Auto-discover servers on your local network</li>
            <li>Waiting state alerts — never miss a tool approval</li>
            <li>Session archiving to keep your dashboard clean</li>
          </ul>
        </div>

        <div className="privacy-section">
          <h2>Install CC Remote Server</h2>
          <p>CC Remote requires a server running on your computer. Choose one of the following methods:</p>

          <h3 className="guide-subtitle">Option 1: macOS Menu Bar App (Recommended)</h3>
          <ol className="guide-list">
            <li>Download <a href="https://github.com/AndyChang33/cc-remote/releases/latest/download/CCRemoteServer-v1.1.0.dmg" className="guide-link">CCRemoteServer.dmg</a> from GitHub</li>
            <li>Open the DMG and drag the app to Applications</li>
            <li>Launch — a terminal icon appears in your menu bar</li>
            <li>Click the icon → <strong>Start Server</strong></li>
          </ol>

          <h3 className="guide-subtitle">Option 2: Command Line</h3>
          <div className="guide-code">
            <code>
              git clone https://github.com/AndyChang33/cc-remote.git<br />
              cd cc-remote<br />
              npm install<br />
              npm start
            </code>
          </div>
          <p>The server runs on port <strong>3456</strong> by default. You can customize it with environment variables:</p>
          <div className="guide-code">
            <code>PORT=8080 CC_WORK_DIR=/path/to/project npm start</code>
          </div>

          <h3 className="guide-subtitle">Option 3: Docker</h3>
          <div className="guide-code">
            <code>
              git clone https://github.com/AndyChang33/cc-remote.git<br />
              cd cc-remote<br />
              docker compose up -d<br />
              node scripts/ccrd.js        # host daemon for terminal sessions
            </code>
          </div>
          <p>The container runs the server (<code className="guide-inline-code">NO_PTY=1</code>), while terminal sessions are spawned on the host via the ccrd daemon. Optional shell aliases:</p>
          <div className="guide-code">
            <code>
              # Add to ~/.zshrc<br />
              alias ccd="/path/to/cc-remote/scripts/cc-docker-start.sh"<br />
              alias ccd-stop="/path/to/cc-remote/scripts/cc-docker-stop.sh"
            </code>
          </div>
        </div>

        <div className="privacy-section">
          <h2>Connect from Your Device</h2>
          <ol className="guide-list">
            <li>Make sure your iPhone/iPad and computer are on the <strong>same WiFi network</strong></li>
            <li>Open the CC Remote app on your device</li>
            <li>The app will auto-discover your server — or enter the address manually (e.g. <code className="guide-inline-code">192.168.x.x:3456</code>)</li>
            <li>Tap to connect and start managing your sessions</li>
          </ol>
        </div>

        <div className="privacy-section">
          <h2>Three Usage Modes</h2>

          <h3 className="guide-subtitle">1. Interactive Terminal</h3>
          <p>Create a new session from the dashboard. You get a full remote terminal — run commands, launch <code className="guide-inline-code">claude</code>, and approve tool calls directly from your phone.</p>

          <h3 className="guide-subtitle">2. Monitor Mode (Hooks)</h3>
          <p>Add Claude Code hooks to report events to the server. The dashboard shows read-only cards with tool call logs and diffs. Add this to your <code className="guide-inline-code">~/.claude/settings.json</code>:</p>
          <div className="guide-code">
            <code>
{`{
  "hooks": {
    "PreToolUse": [{ "hooks": [{ "type": "command",
      "command": "curl -s -X POST http://localhost:3456/hook -H 'Content-Type: application/json' -d @- > /dev/null 2>&1 || true" }] }],
    "PostToolUse": [{ "hooks": [{ "type": "command",
      "command": "curl -s -X POST http://localhost:3456/hook -H 'Content-Type: application/json' -d @- > /dev/null 2>&1 || true" }] }],
    "Notification": [{ "hooks": [{ "type": "command",
      "command": "curl -s -X POST http://localhost:3456/hook -H 'Content-Type: application/json' -d @- > /dev/null 2>&1 || true" }] }]
  }
}`}
            </code>
          </div>

          <h3 className="guide-subtitle">3. Proxy Mode (ccr)</h3>
          <p>Use <code className="guide-inline-code">ccr</code> instead of <code className="guide-inline-code">claude</code> in your terminal. The web/app view mirrors your terminal output exactly, and you can send input from your phone — including y/n confirmations.</p>
          <div className="guide-code">
            <code>
              ccr                    # equivalent to claude<br />
              ccr --resume           # resume last conversation
            </code>
          </div>
        </div>

        <div className="privacy-section">
          <h2>Public Access (Optional)</h2>
          <p>By default, the server is only accessible on your local network. To access it from anywhere, use ngrok:</p>
          <div className="guide-code">
            <code>
              npm start<br />
              ngrok http 3456
            </code>
          </div>
          <p>For security, consider adding authentication:</p>
          <div className="guide-code">
            <code>ngrok http 3456 --basic-auth="user:yourpassword"</code>
          </div>
        </div>

        <div className="privacy-section">
          <h2>Learn More</h2>
          <p>CC Remote server is open source. Visit the <a href="https://github.com/AndyChang33/cc-remote" className="guide-link">GitHub repository</a> for full documentation, issue tracking, and contributions.</p>
        </div>
      </main>
    </div>
  );
};

export default CCRemoteGuide;
